import type { MemberRequest } from "@features/authentication/types";
import type {
  OrderBillingAddressRequest,
  OrderCancelRequest,
  OrderCancelResponse,
  OrderCheckoutRequest,
  OrderCheckoutResponse,
  OrderDetail,
  OrderListReponse,
  OrderShippingAddressRequest,
  OrderUpdateAddressResponse,
} from "@features/shopping/types";

import { api } from "@services/api";
import { decryptData, encryptData } from "@shared/utils/encryption";

export class OrderService {
  private readonly basePath = "/public/order";

  // Cancel order
  public async cancelOrder(orderId: string): Promise<OrderCancelResponse> {
    const request: OrderCancelRequest = {
      order_id: orderId,
    };
    const response = await api.post<OrderCancelResponse>("/order/cancel", request);

    return response;
  }

  // Checkout order
  public async checkoutOrder(data: OrderCheckoutRequest): Promise<OrderCheckoutResponse> {
    const response = await api.post<{ data: string }>(`${this.basePath}/checkout`, {
      data: await encryptData(data),
    });

    return await decryptData(response.data);
  }

  // Get product details
  public async getOrderDetails(orderId: string): Promise<OrderDetail> {
    const response = await api.post<{ data: string }>(`${this.basePath}/detail`, {
      data: await encryptData({
        order_id: orderId,
      }),
    });

    return await decryptData<OrderDetail>(response.data);
  }

  // Get order list
  public async getOrderList(memberId: string): Promise<OrderListReponse> {
    const request: MemberRequest = {
      member_id: memberId,
    };
    const response = await api.post<OrderListReponse>("/orders", request);

    return response;
  }

  // Update order billing address
  public async updateOrderBillingAddress(
    data: OrderBillingAddressRequest,
  ): Promise<OrderUpdateAddressResponse> {
    const response = await api.post<{ data: string }>(`${this.basePath}/billing/address`, {
      data: await encryptData(data),
    });

    return await decryptData<OrderUpdateAddressResponse>(response.data);
  }

  // Update order shipping address
  public async updateOrderShippingAddress(
    data: OrderShippingAddressRequest,
  ): Promise<OrderUpdateAddressResponse> {
    const response = await api.post<{ data: string }>(`${this.basePath}/shipping/address`, {
      data: await encryptData(data),
    });

    return await decryptData<OrderUpdateAddressResponse>(response.data);
  }
}

export const orderService = new OrderService();
